import { CallToActionType } from 'modules/Campaign'
import { PlaybookType, AdPlatform } from 'modules/Const'

/**
 * Centralized mock data for Assistant workspace demos
 * This file contains all the demo data needed for testing UI components
 * without having to go through the chat flow each time.
 */

// Base demo content by industry type
export const DEMO_CONTENT = {
  music: {
    brand: 'Luna Rose',
    product: 'upcoming single "Midnight Dreams"',
    audience: 'indie pop fans aged 18-34',
    platforms: 'Spotify, Apple Music, Instagram',
    budget: '$2,500',
    goals: 'increase streams and build fanbase',
    website: 'https://lunarosemusic.com',
    instagram: '@lunarosemusic',
    email: 'luna@lunarosemusic.com'
  },
  ecommerce: {
    brand: 'EcoStyle Boutique',
    product: 'sustainable fashion collection',
    audience: 'eco-conscious shoppers aged 25-45',
    platforms: 'Instagram, Facebook, TikTok',
    budget: '$5,000',
    goals: 'drive sales and brand awareness',
    website: 'https://ecostyleboutique.com',
    instagram: '@ecostyleboutique',
    email: 'hello@ecostyleboutique.com'
  },
  content_creator: {
    brand: 'FitLife Coach',
    product: 'online fitness program',
    audience: 'fitness enthusiasts aged 20-40',
    platforms: 'YouTube, Instagram, TikTok',
    budget: '$3,000',
    goals: 'increase course signups and followers',
    website: 'https://fitlifecoach.com',
    instagram: '@fitlifecoach',
    email: 'coach@fitlifecoach.com'
  }
} as const

// Common demo assets
export const DEMO_ASSETS = {
  image1: 'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png',
  image2: 'https://res.cloudinary.com/symphony-images/image/upload/v1700083256/symphony/brand_content/sample_image_2.png',
  video1: 'https://res.cloudinary.com/symphony-images/video/upload/v1700083091/symphony/brand_content/demo_video.mp4',
  thumbnail1: 'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png'
}

// Campaign Approval Demo Data
export const createCampaignApprovalMockData = (demoType: keyof typeof DEMO_CONTENT = 'music', baseTime: number = Date.now()) => {
  const content = DEMO_CONTENT[demoType]
  
  return {
    campaignId: `demo-campaign-${baseTime}`,
    isDemo: true,
    demoCampaign: {
      id: `demo-campaign-${baseTime}`,
      name: `${content.brand} - 2024 Campaign`,
      playbook_type: demoType === 'music' ? PlaybookType.INCREASE_MUSIC_STREAMS : 
                     demoType === 'ecommerce' ? PlaybookType.INCREASE_LINK_CLICKS : PlaybookType.META_EMAIL_SIGNUPS,
      status: 'READY_FOR_SUBMISSION',
      goal_link: content.website,
      agent_notes: {
        strategy_summary: `Comprehensive ${content.platforms} campaign to ${content.goals} with ${content.budget} budget`,
        one_liner: demoType === 'music' ? 'Dreamy indie vibes for late-night music discovery' :
                   demoType === 'ecommerce' ? 'Sustainable fashion for conscious consumers' :
                   'Transform your fitness journey with expert guidance',
        targeted_audiences: demoType === 'music' ? ['Indie Music Fans', 'Alternative Rock Listeners'] :
                           demoType === 'ecommerce' ? ['Sustainable Fashion', 'Eco-Conscious Shoppers'] :
                           ['Fitness Enthusiasts', 'Health Conscious'],
        targeted_locations: ['United States', 'Canada', 'United Kingdom'],
        estimation_expectations: 'Expecting strong performance in target demographics',
        budget_guidance: `${content.budget} budget optimally allocated across platforms`
      },
      campaign_input: {
        platform: AdPlatform.META,
        budget: parseInt(content.budget.replace(/\D/g, '')),
        budgetType: 'lifetime' as const,
        length: {
          startDate: new Date().toISOString(),
          endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        },
        creatives: [{
          source: {
            type: 'uploaded_asset' as const,
            mediaUrl: DEMO_ASSETS.image1,
            thumbnailUrl: DEMO_ASSETS.thumbnail1,
            cloudinaryId: 'demo-asset',
            assetType: 'image' as const
          },
          caption: `Discover ${content.product} - ${demoType === 'music' ? 'Stream now on all platforms' : 
                   demoType === 'ecommerce' ? 'Shop sustainable fashion' : 'Start your fitness transformation'}`,
          cta: {
            label: demoType === 'music' ? 'Listen Now' : demoType === 'ecommerce' ? 'Shop Now' : 'Learn More',
            value: 'LEARN_MORE'
          } as CallToActionType
        }],
        goalLink: content.website,
        campaignName: `${content.brand} - 2024 Campaign`,
        playbookType: demoType === 'music' ? PlaybookType.INCREASE_MUSIC_STREAMS : 
                      demoType === 'ecommerce' ? PlaybookType.INCREASE_LINK_CLICKS : PlaybookType.META_EMAIL_SIGNUPS,
        totalAdsBudget: parseInt(content.budget.replace(/\D/g, ''))
      },
      readyForSubmission: true,
      checkoutDetails: {
        totalCost: parseInt(content.budget.replace(/\D/g, '')) * 1.2,
        adsBudget: parseInt(content.budget.replace(/\D/g, '')),
        takeRate: parseInt(content.budget.replace(/\D/g, '')) * 0.2,
        lineItems: [
          { type: 'ads', name: 'Ad Spend', description: 'Meta Ads', amount: parseInt(content.budget.replace(/\D/g, '')) },
          { type: 'fee', name: 'Symphony Fee', description: 'Platform Fee', amount: parseInt(content.budget.replace(/\D/g, '')) * 0.2 }
        ],
        currency: 'USD',
        stripeSession: {
          paid: false,
          payment_intent_id: 'pi_demotest',
          client_secret: 'pi_demotest_secret_demotest'
        }
      }
    }
  }
}

// Content Analysis Demo Data
export const createContentAnalysisMockData = (demoType: keyof typeof DEMO_CONTENT = 'music', baseTime: number = Date.now()) => {
  const content = DEMO_CONTENT[demoType]
  
  return {
    analysisId: `demo-analysis-${baseTime}`,
    isDemo: true,
    demoAnalysisResult: {
      success: true,
      analysis: {
        username: content.instagram.replace('@', ''),
        profileData: {
          followers: demoType === 'music' ? 15400 : demoType === 'ecommerce' ? 8200 : 12100,
          postsCount: demoType === 'music' ? 127 : demoType === 'ecommerce' ? 89 : 156,
          following: 892,
          bio: demoType === 'music' ? 'Indie artist crafting dreamy melodies 🌙✨' :
               demoType === 'ecommerce' ? 'Sustainable fashion for conscious living 🌱' :
               'Your fitness transformation starts here 💪',
          profilePicture: DEMO_ASSETS.image1
        },
        createdAt: new Date(baseTime).toISOString(),
        recommendations: {
          contentThemes: demoType === 'music' ? ['Behind the Scenes', 'Live Performances', 'Music Production'] :
                        demoType === 'ecommerce' ? ['Product Styling', 'Sustainability Tips', 'Customer Features'] :
                        ['Workout Tutorials', 'Nutrition Tips', 'Transformation Stories'],
          visualElements: demoType === 'music' ? ['Moody Lighting', 'Studio Shots', 'Urban Backgrounds'] :
                         demoType === 'ecommerce' ? ['Clean Backgrounds', 'Natural Lighting', 'Lifestyle Shots'] :
                         ['Action Shots', 'Before/After', 'Equipment Close-ups'],
          contentPatterns: ['Short Form Video', 'Carousel Posts', 'Story Highlights'],
          recommendations: [
            'Post more consistently during peak engagement hours',
            'Engage with comments within the first hour',
            'Use trending hashtags in your niche'
          ],
          contentIdeas: demoType === 'music' ? ['Day in the studio', 'Song inspiration story', 'Equipment tour'] :
                       demoType === 'ecommerce' ? ['Styling challenge', 'Sustainability journey', 'Customer spotlight'] :
                       ['Morning routine', 'Workout form tips', 'Meal prep guide']
        },
        analyzedPosts: [
          {
            postMetadata: {
              caption: demoType === 'music' ? 'New single "Midnight Dreams" drops Friday! 🌙 What\'s your favorite time to discover new music?' :
                      demoType === 'ecommerce' ? 'Sustainable fashion isn\'t just a trend - it\'s a lifestyle choice 🌱 #EcoFashion' :
                      'Morning workout complete! 💪 Remember: consistency beats perfection every time',
              username: content.instagram.replace('@', ''),
              timestamp: new Date(baseTime - 86400000).toISOString(),
              permalink: '#demo-post-1',
              engagement: demoType === 'music' ? 342 : demoType === 'ecommerce' ? 189 : 428,
              thumbnailUrl: DEMO_ASSETS.image1
            },
            analysis: {
              detailed_summary: demoType === 'music' ? 'This post performed exceptionally well due to the anticipation-building caption and strategic use of the moon emoji, which aligns with your brand aesthetic.' :
                               demoType === 'ecommerce' ? 'Strong performance driven by authentic messaging about sustainability values and relevant hashtag usage.' :
                               'High engagement from motivational content and relatable fitness messaging that resonates with your audience.',
              content_breakdown: {
                themes_topics: demoType === 'music' ? ['New Release', 'Music Discovery', 'Late Night Vibes'] :
                              demoType === 'ecommerce' ? ['Sustainable Fashion', 'Lifestyle Choice', 'Brand Values'] :
                              ['Morning Routine', 'Fitness Motivation', 'Consistency'],
                video_narrative: '',
                visual_elements: demoType === 'music' ? ['Moody lighting', 'Studio setting'] :
                                demoType === 'ecommerce' ? ['Clean aesthetic', 'Product focus'] :
                                ['Action shot', 'Gym setting']
              },
              target_audience_analysis: {
                demographics: [content.audience.split(' aged ')[0]],
                psychographics: demoType === 'music' ? ['Music lovers', 'Late night listeners'] :
                               demoType === 'ecommerce' ? ['Environmentally conscious', 'Quality-focused'] :
                               ['Health-conscious', 'Goal-oriented'],
                public_figures_brands: [],
                topics: demoType === 'music' ? ['Music discovery', 'Indie music'] :
                       demoType === 'ecommerce' ? ['Sustainable living', 'Fashion'] :
                       ['Fitness', 'Health']
              }
            }
          },
          {
            postMetadata: {
              caption: demoType === 'music' ? 'Behind the scenes from yesterday\'s recording session ✨ The magic happens in these quiet moments' :
                      demoType === 'ecommerce' ? 'From concept to closet: the journey of our latest sustainable collection 👗' :
                      'Form check: proper squat technique can make all the difference in your results 🏋️‍♀️',
              username: content.instagram.replace('@', ''),
              timestamp: new Date(baseTime - 172800000).toISOString(),
              permalink: '#demo-post-2',
              engagement: demoType === 'music' ? 298 : demoType === 'ecommerce' ? 156 : 389,
              thumbnailUrl: DEMO_ASSETS.image2
            },
            analysis: {
              detailed_summary: demoType === 'music' ? 'Behind-the-scenes content always performs well with your audience, showing the authentic creative process.' :
                               demoType === 'ecommerce' ? 'Educational content about your process builds trust and transparency with customers.' :
                               'Educational fitness content positions you as an expert and provides real value to followers.',
              content_breakdown: {
                themes_topics: demoType === 'music' ? ['Behind the Scenes', 'Creative Process', 'Studio Life'] :
                              demoType === 'ecommerce' ? ['Product Development', 'Transparency', 'Process'] :
                              ['Education', 'Proper Form', 'Technique'],
                video_narrative: demoType === 'music' ? 'Studio recording process' :
                                demoType === 'ecommerce' ? 'Design to production journey' :
                                'Exercise demonstration',
                visual_elements: demoType === 'music' ? ['Studio equipment', 'Work in progress'] :
                                demoType === 'ecommerce' ? ['Design sketches', 'Materials'] :
                                ['Exercise demonstration', 'Form focus']
              },
              target_audience_analysis: {
                demographics: [content.audience.split(' aged ')[0]],
                psychographics: demoType === 'music' ? ['Creative minded', 'Process interested'] :
                               demoType === 'ecommerce' ? ['Transparency seekers', 'Process curious'] :
                               ['Learning focused', 'Improvement minded'],
                public_figures_brands: [],
                topics: demoType === 'music' ? ['Music production', 'Creative process'] :
                       demoType === 'ecommerce' ? ['Fashion design', 'Sustainability'] :
                       ['Exercise technique', 'Fitness education']
              }
            }
          }
        ]
      }
    }
  }
}

// Marketing Plan Demo Data
export const createMarketingPlanMockData = (demoType: keyof typeof DEMO_CONTENT = 'music', baseTime: number = Date.now()) => {
  const content = DEMO_CONTENT[demoType]
  const budget = parseInt(content.budget.replace(/\D/g, ''))
  
  return {
    planId: `demo-plan-${baseTime}`,
    name: `${content.brand} Marketing Strategy`,
    status: 'active',
    type: 'launch',
    isDemo: true,
    demoMarketingPlan: {
      id: `demo-plan-${baseTime}`,
      brandId: 12345,
      name: `${content.brand} Marketing Strategy`,
      type: 'launch' as const,
      plan_type: 'launch',
      objective: `Launch and promote ${content.product} to ${content.goals}`,
      target_outcomes: [
        'Increase brand awareness by 50%',
        `Generate ${demoType === 'music' ? '10K new followers' : demoType === 'ecommerce' ? '5K new customers' : '2K course signups'}`,
        'Achieve 5% engagement rate',
        `Drive ${demoType === 'music' ? '2.5K streams' : demoType === 'ecommerce' ? '1K website visits' : '500 consultations'}`
      ],
      audience: {
        primaryPersona: {
          label: demoType === 'music' ? 'Indie Music Lovers' :
                 demoType === 'ecommerce' ? 'Conscious Shoppers' : 'Fitness Enthusiasts',
          age: content.audience.split(' aged ')[1] || '18-34',
          demographics: content.audience.split(' aged ')[0]?.split(', ') || ['Urban professionals'],
          geographies: ['United States', 'Canada', 'United Kingdom'],
          interests: demoType === 'music' ? ['Indie Music', 'Alternative Rock', 'Live Music', 'Music Discovery'] :
                    demoType === 'ecommerce' ? ['Sustainable Fashion', 'Eco-Friendly Products', 'Conscious Shopping'] :
                    ['Fitness', 'Health & Wellness', 'Personal Training', 'Workout Routines']
        }
      },
      budget: {
        total: budget,
        tier: budget > 5000 ? 'large' : budget > 2000 ? 'medium' : 'small',
        allocation: {
          ads: Math.floor(budget * 0.6),
          content: Math.floor(budget * 0.25),
          tools: Math.floor(budget * 0.15)
        }
      },
      timeline: {
        phases: [
          {
            name: 'Setup & Creative Development',
            startDate: new Date(baseTime).toISOString(),
            endDate: new Date(baseTime + 7 * 24 * 60 * 60 * 1000).toISOString(),
            focus: 'Brand setup and content creation',
            actions: [
              {
                id: 'action-1',
                type: 'symphony_tool' as const,
                title: 'Create Social Media Assets',
                description: 'Generate branded visuals and promotional content',
                platform: 'Instagram',
                symphonyTool: 'Creative Generator',
                estimatedTime: '2 hours',
                priority: 'high' as const,
                status: 'completed' as const
              },
              {
                id: 'action-2',
                type: 'organic_content' as const,
                title: 'Write Campaign Copy',
                description: 'Develop compelling captions and ad copy',
                estimatedTime: '3 hours',
                priority: 'high' as const,
                status: 'completed' as const
              }
            ]
          },
          {
            name: 'Launch Phase',
            startDate: new Date(baseTime + 7 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date(baseTime + 21 * 24 * 60 * 60 * 1000).toISOString(),
            focus: 'Campaign activation and monitoring',
            actions: [
              {
                id: 'action-3',
                type: 'symphony_tool' as const,
                title: `Launch ${demoType === 'music' ? 'Music Promotion' : demoType === 'ecommerce' ? 'Sales' : 'Lead Generation'} Campaign`,
                description: 'Activate paid advertising on Instagram and Facebook',
                platform: 'Instagram',
                symphonyTool: 'Campaign Manager',
                budget: Math.floor(budget * 0.4),
                estimatedTime: '1 hour',
                priority: 'high' as const,
                status: 'in_progress' as const
              },
              {
                id: 'action-4',
                type: 'organic_content' as const,
                title: 'Daily Social Media Posts',
                description: 'Post engaging content to build organic reach',
                platform: content.platforms,
                estimatedTime: '30 min/day',
                priority: 'medium' as const,
                status: 'in_progress' as const
              }
            ]
          },
          {
            name: 'Optimization & Scale',
            startDate: new Date(baseTime + 21 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date(baseTime + 37 * 24 * 60 * 60 * 1000).toISOString(),
            focus: 'Performance optimization and scaling',
            actions: [
              {
                id: 'action-5',
                type: 'symphony_tool' as const,
                title: 'A/B Test Ad Creatives',
                description: 'Test different creative variations for optimal performance',
                platform: 'Instagram',
                symphonyTool: 'Performance Optimizer',
                estimatedTime: '2 hours',
                priority: 'medium' as const,
                status: 'pending' as const
              },
              {
                id: 'action-6',
                type: 'external_strategy' as const,
                title: demoType === 'music' ? 'Playlist Pitching' : demoType === 'ecommerce' ? 'Influencer Outreach' : 'Partnership Development',
                description: demoType === 'music' ? 'Submit to relevant Spotify playlists' :
                           demoType === 'ecommerce' ? 'Partner with sustainable fashion influencers' :
                           'Collaborate with fitness professionals',
                estimatedTime: '4 hours',
                priority: 'low' as const,
                status: 'pending' as const
              }
            ]
          }
        ]
      },
      createdAt: new Date(baseTime).toISOString(),
      updatedAt: new Date(baseTime).toISOString(),
      status: 'active' as const
    }
  }
}

// Facebook Connect Demo Data
export const createFacebookConnectMockData = () => ({
  reason: 'Connect Facebook to run targeted ads and track conversions for your campaign.',
  nextStep: 'pixel_setup',
  skipOption: true
})

// Instagram Post Selection Demo Data
export const createInstagramPostSelectionMockData = (baseTime: number = Date.now()) => ({
  campaignId: `demo-ig-campaign-${baseTime}`,
  isDemo: true
})

// Subscription Flow Demo Data
export const createSubscriptionFlowMockData = (demoType: keyof typeof DEMO_CONTENT = 'music') => {
  const content = DEMO_CONTENT[demoType]
  const budget = parseInt(content.budget.replace(/\D/g, ''))
  
  return {
    config: {
      flowType: 'campaign_approval',
      title: 'Upgrade to Launch Your Campaign',
      description: 'Launch your campaign and start growing your audience today.',
      upgradeCallToAction: 'Start Growing',
      highlightedPlan: 'GROWTH' as const,
      source: 'DemoMode_SubscriptionFlow',
      campaignPricing: {
        totalCost: budget * 1.2,
        adsBudget: budget,
        takeRate: budget * 0.2,
        lineItems: [
          { type: 'ads', name: 'Ad Spend', amount: budget, description: 'Meta Advertising' },
          { type: 'fee', name: 'Symphony Fee', amount: budget * 0.2, description: 'Platform Fee' }
        ],
        currency: 'USD'
      }
    },
    isDemo: true
  }
}

// Template Selection Demo Data
export const createTemplateSelectionMockData = (demoType: keyof typeof DEMO_CONTENT = 'music') => ({
  templateCategories: demoType === 'music' ? ['music'] :
                     demoType === 'ecommerce' ? ['popular', 'ecommerce'] :
                     ['popular', 'fitness'],
  isDemo: true
})

// Website Preview Demo Data
export const createWebsitePreviewMockData = (demoType: keyof typeof DEMO_CONTENT = 'music', baseTime: number = Date.now()) => {
  const content = DEMO_CONTENT[demoType]
  
  return {
    websiteId: `demo-website-${baseTime}`,
    slug: `demo-website-${baseTime}`,
    status: 'DRAFT',
    isDemo: true,
    websiteContent: {
      content: [
        {
          id: '1',
          type: 'TEXT',
          contentIndex: 0,
          textContent: {
            title: content.brand,
            subtitle: demoType === 'music' ? 'Dreamy indie music for late-night listening' :
                     demoType === 'ecommerce' ? 'Sustainable fashion for conscious living' :
                     'Transform your fitness journey with expert guidance'
          }
        },
        {
          id: '2',
          type: 'IMAGE',
          contentIndex: 1,
          imageContent: {
            src: DEMO_ASSETS.image1,
            alt: `${content.brand} hero image`
          }
        },
        {
          id: '3',
          type: 'TEXT',
          contentIndex: 2,
          textContent: {
            title: demoType === 'music' ? 'Latest Release' : demoType === 'ecommerce' ? 'New Collection' : 'Featured Program',
            subtitle: demoType === 'music' ? 'Stream "Midnight Dreams" now on all platforms' :
                     demoType === 'ecommerce' ? 'Discover our sustainable fashion collection' :
                     'Join thousands who have transformed their fitness'
          }
        }
      ]
    },
    websiteStyle: {
      backgroundColor: demoType === 'music' ? '#1a1a2e' : demoType === 'ecommerce' ? '#f8f9fa' : '#ffffff',
      secondaryColor: demoType === 'music' ? '#16213e' : demoType === 'ecommerce' ? '#28a745' : '#007bff',
      textColor: demoType === 'music' ? '#eee' : '#333333',
      fontFamily: demoType === 'music' ? 'Georgia, serif' : demoType === 'ecommerce' ? 'Helvetica, sans-serif' : 'Arial, sans-serif',
      textAlign: 'center' as const,
      isSymphonyLogoEnabled: true,
      symphonyLogoStyle: demoType === 'music' ? 'light' : 'dark'
    }
  }
}

// All available workspace types for easy reference
export const AVAILABLE_WORKSPACES = [
  'campaignPreview',
  'contentAnalysis', 
  'facebookConnect',
  'instagramPostSelection',
  'marketingPlan',
  'subscriptionFlow',
  'templateSelection',
  'websitePreview'
] as const

export type WorkspaceType = typeof AVAILABLE_WORKSPACES[number]

// Demo scenario generator - creates complete demo scenarios for any workspace
export const createDemoScenario = (
  workspaceType: WorkspaceType,
  demoType: keyof typeof DEMO_CONTENT = 'music',
  scenarioId?: string
) => {
  const baseTime = Date.now()
  const content = DEMO_CONTENT[demoType]
  const id = scenarioId || `demo_${workspaceType}_${demoType}`

  // Base scenario structure
  const baseScenario = {
    id,
    name: `${workspaceType} Demo - ${content.brand}`,
    description: `Testing ${workspaceType} with ${demoType} industry data`,
    demoType,
    messages: [] // No messages needed for direct workspace testing
  }

  // Generate workspace-specific data
  let workspaceData
  switch (workspaceType) {
    case 'campaignPreview':
      workspaceData = createCampaignApprovalMockData(demoType, baseTime)
      break
    case 'contentAnalysis':
      workspaceData = createContentAnalysisMockData(demoType, baseTime)
      break
    case 'facebookConnect':
      workspaceData = createFacebookConnectMockData()
      break
    case 'instagramPostSelection':
      workspaceData = createInstagramPostSelectionMockData(baseTime)
      break
    case 'marketingPlan':
      workspaceData = createMarketingPlanMockData(demoType, baseTime)
      break
    case 'subscriptionFlow':
      workspaceData = createSubscriptionFlowMockData(demoType)
      break
    case 'templateSelection':
      workspaceData = createTemplateSelectionMockData(demoType)
      break
    case 'websitePreview':
      workspaceData = createWebsitePreviewMockData(demoType, baseTime)
      break
    default:
      workspaceData = { isDemo: true }
  }

  return {
    ...baseScenario,
    activeWorkspace: {
      type: workspaceType,
      data: workspaceData
    }
  }
}